import CustomerService from '@/services/CustomerService'
import { buildCustomerListRequest } from '@/lib/customer-list-request'
import { cloneDeep } from 'lodash-es'
import Vue from 'vue'

const DEFAULT_DATE_FILTER = {
  date: 30,
  defaultValue: 90,
  name: 'In the last',
  type: 'ago',
  unit: 'days',
  tzOffset: new Date().getTimezoneOffset()
}

/**
 * The count is published as its own message and can be `null`, which is a routine outcome on the
 * large companies this endpoint exists for. Three states rather than a nullable number, so that a
 * genuine zero-match result stays distinguishable from a count that has not arrived.
 */
export const COUNT_STATUS = {
  PENDING: 'pending',
  ARRIVED: 'arrived',
  ABSENT: 'absent'
}

/**
 * How long to wait for the websocket delivery before giving up on a request.
 *
 * The dispatch POST resolves as soon as the request is queued, so it can no longer reject and the
 * page has no timeout of its own any more. The worker is `timeout: 60` and a Lambda timeout runs no
 * catch block, so past that point neither phase can still arrive; this sits above it so a slow but
 * successful delivery is never cut off.
 */
const DELIVERY_TIMEOUT_MS = 75000

const THROTTLED_DISPATCH_ERROR = 'The customer list is busy. Try again in a moment.'
const DEFAULT_DISPATCH_ERROR = 'Unable to load customers. Please try again.'
const DEFAULT_DELIVERY_ERROR = 'Could not load the customer list.'

/**
 * The endpoint's own `skip` ceiling. Past it the request is a 400 rather than a page, so the page
 * stops offering one instead of surfacing an error on a table that was working.
 */
const MAX_ASYNC_SKIP = 10000

// Module scope rather than store state: it is a handle to cancel, not something the UI renders.
let deliveryTimeoutId = null

/**
 * Claimed by each dispatch before its first await, so one that resolves late can tell it has been
 * superseded. `activeRequestId` cannot answer that on its own: it is only known once the POST
 * resolves, so two overlapping dispatches race to set it and the slower ack wins.
 */
let dispatchSequence = 0

const clearDeliveryTimeout = () => {
  if (deliveryTimeoutId) clearTimeout(deliveryTimeoutId)
  deliveryTimeoutId = null
}

/** Both phases have settled, so the timer has nothing left to guard. */
const clearDeliveryTimeoutWhenSettled = (state) => {
  if (!state.isSearching && state.countStatus !== COUNT_STATUS.PENDING) clearDeliveryTimeout()
}

const assembleChunks = (pendingChunks) =>
  Object.keys(pendingChunks)
    .map(Number)
    .sort((a, b) => a - b)
    .reduce((records, key) => records.concat(pendingChunks[key]), [])

const dispatchErrorText = (error) =>
  error?.body?.message ||
  (error?.status === 429 ? THROTTLED_DISPATCH_ERROR : DEFAULT_DISPATCH_ERROR)

export default {
  namespaced: true,
  state: {
    customers: [],
    listFilters: { dateSettings: DEFAULT_DATE_FILTER },
    skipCount: 0,
    limit: 50,
    count: 0,
    isSearching: false,
    listError: false,
    metrics: {},
    importParams: {},
    // Set from CustomersTableCard's `created` hook. That is sound only because CustomersTableFilters
    // and CustomersTableList are used nowhere else, so every dispatcher of queryCustomers has run it
    // first. Anything that mounts either of them directly inherits the previous screen's path.
    useAsyncList: false,
    activeRequestId: null,
    pendingChunks: {},
    expectedChunkCount: null,
    pendingLoadMore: false,
    lastPageSize: 0,
    countStatus: COUNT_STATUS.PENDING,
    // The records phase completes once per request, but `activeRequestId` has to stay live past it
    // for the count, which publishes separately. Without this a redelivered records message matches
    // the still-active id and appends its rows a second time.
    recordsComplete: false
  },
  getters: {
    selectIsSearching(state) {
      return state.isSearching
    },
    selectCountStatus: (state) => state.countStatus,
    selectIsCountPending: (state) => state.countStatus === COUNT_STATUS.PENDING,
    selectIsCountAvailable: (state) => state.countStatus === COUNT_STATUS.ARRIVED,
    /**
     * There is no total to compare against when the count is absent or still in flight, so fall
     * back to whether the last page came back full. The compare is against the assembled page, not
     * a single websocket message: one page arrives as several chunks, so no chunk's own length
     * says anything about whether the page was full.
     */
    selectHasMore: (state) => {
      // Only the async endpoint bounds the offset; the nexus path has no such ceiling and keeps its
      // existing behaviour. Compared bare rather than with a page added: `skipCount` is set from the
      // worker's echo, which is already the next request's offset.
      if (state.useAsyncList && state.skipCount > MAX_ASYNC_SKIP) return false

      // The nexus path never sets lastPageSize (only the async records handler does), so it has to
      // keep the compare it always used rather than fall into the no-count branch below.
      if (!state.useAsyncList) return state.count > state.customers.length

      return state.countStatus === COUNT_STATUS.ARRIVED
        ? state.count > state.customers.length
        : state.lastPageSize === state.limit
    },
    selectListError(state) {
      return state.listError
    },
    selectCustomerById: (state) => (customerId) =>
      state.customers.find((c) => c._id === customerId),
    selectMetrics: (state) => state.metrics,
    selectImportParams: (state) => state.importParams,
    selectCount: (state) => state.count,
    selectCustomers: (state) => state.customers,
    selectListFilters: (state) => state.listFilters
  },
  mutations: {
    addCustomers(state, customers) {
      state.customers = state.customers.concat(customers)
    },
    setCustomers(state, customers) {
      state.customers = [...customers]
    },
    setListFilters(state, filters) {
      state.listFilters = { ...filters }
    },
    resetListFilters(state) {
      state.listFilters = { dateSettings: DEFAULT_DATE_FILTER }
    },
    setSkipCount(state, skip) {
      state.skipCount = skip
    },
    setLimit(state, limit) {
      state.limit = limit
    },
    setCount(state, count) {
      state.count = count
    },
    setIsSearching(state, isSearching) {
      state.isSearching = isSearching
    },
    setListError(state, hasError) {
      state.listError = hasError
    },
    updateCustomer(state, customer) {
      state.customers = state.customers.map((c) => (c._id === customer._id ? customer : c))
    },
    SET_METRICS(state, metrics) {
      state.metrics = metrics
    },
    SET_IMPORT_PARAMS(state, importParams) {
      state.importParams = importParams
    },
    SET_LIST_FILTERS(state, filters) {
      state.listFilters = { ...filters }
    },
    SET_SKIP_COUNT(state, skip) {
      state.skipCount = skip
    },
    setUseAsyncList(state, useAsyncList) {
      state.useAsyncList = useAsyncList
    },
    setActiveRequestId(state, requestId) {
      state.activeRequestId = requestId
    },
    setPendingLoadMore(state, loadMore) {
      state.pendingLoadMore = loadMore
    },
    resetPendingChunks(state) {
      state.pendingChunks = {}
      state.expectedChunkCount = null
    },
    addPendingChunk(state, { chunk, records, chunkCount }) {
      state.pendingChunks = { ...state.pendingChunks, [chunk]: records }
      // Only when the message carried one. Assigning `undefined` makes the completion check compare
      // against it, which is false, i.e. "complete" — the wrong default for a guard whose whole job
      // is to notice a page that stopped early.
      if (chunkCount != null) state.expectedChunkCount = chunkCount
    },
    setRecordsComplete(state, complete) {
      state.recordsComplete = complete
    },
    setLastPageSize(state, size) {
      state.lastPageSize = size
    },
    setCountStatus(state, status) {
      state.countStatus = status
    }
  },
  actions: {
    async queryCustomers({ dispatch, state }, { loadMore }) {
      return state.useAsyncList
        ? dispatch('queryCustomersAsync', { loadMore })
        : dispatch('queryCustomersFromNexus', { loadMore })
    },

    async queryCustomersFromNexus({ commit, state, rootState }, { loadMore }) {
      commit('setIsSearching', true)
      commit('setListError', false)
      commit('setCountStatus', COUNT_STATUS.PENDING)
      try {
        let filters = cloneDeep(state.listFilters)
        if (filters.search) filters.dateSettings = {}
        const response = await CustomerService.list({
          companyId: rootState.company.activeCompany._id,
          filters,
          skip: state.skipCount,
          limit: state.limit
        })

        const { customers, skip, count } = response.body

        if (loadMore) {
          if (customers.length !== 0) {
            commit('addCustomers', customers)
          }
        } else {
          commit('setCustomers', customers)
        }

        commit('setCount', count)
        commit('setCountStatus', COUNT_STATUS.ARRIVED)
        commit('setSkipCount', skip)
      } catch (error) {
        console.error('Failed to load customers', error)
        commit('setListError', true)
        commit('setCountStatus', COUNT_STATUS.ABSENT)
        if (!loadMore) {
          commit('setCustomers', [])
          commit('setCount', 0)
        }
      } finally {
        commit('setIsSearching', false)
      }
    },

    /**
     * Dispatches a list request and returns; the rows and the count arrive later over the user's
     * websocket channel and land in `receiveListRecords` / `receiveListCount`.
     */
    async queryCustomersAsync({ commit, dispatch, state, rootState, rootGetters }, { loadMore }) {
      commit('setIsSearching', true)
      commit('setListError', false)
      commit('setCountStatus', COUNT_STATUS.PENDING)
      commit('setPendingLoadMore', !!loadMore)
      commit('resetPendingChunks')
      commit('setActiveRequestId', null)
      commit('setRecordsComplete', false)
      clearDeliveryTimeout()

      // Claimed before the first await. Everything below only settles shared state while this is
      // still the newest dispatch, so a request whose POST acks after a newer one started cannot
      // take the correlation id and answer for filters the user has already moved past.
      const dispatchId = ++dispatchSequence
      const superseded = () => dispatchId !== dispatchSequence

      try {
        // Pusher does not replay to a client that subscribes after a publish, so a request sent
        // before the channel is up loses both phases and the page waits forever.
        const subscribed = await dispatch('websocketV2/awaitUserChannelReady', null, { root: true })
        if (superseded()) return
        if (!subscribed) {
          Vue.notify({ type: 'error', text: DEFAULT_DELIVERY_ERROR })
          commit('setCountStatus', COUNT_STATUS.ABSENT)
          commit('setListError', true)
          commit('setIsSearching', false)
          // Otherwise the table keeps rendering whatever the previous filter left behind as though
          // it belonged to this request; every other exit from this action clears or notifies.
          if (!loadMore) {
            commit('setCustomers', [])
            commit('setCount', 0)
          }
          return
        }

        // A search is a lookup across the whole customer base, not a lookup within the current date
        // window. The nexus path has always blanked `dateSettings` for exactly this reason, and
        // `listFilters` defaults to "In the last 30 days", so without this a search silently misses
        // every customer added before that window.
        const listFilters = state.listFilters.search
          ? { ...state.listFilters, dateSettings: {} }
          : state.listFilters

        // Built after the wait, not before it, so a filter change committed during the wait is
        // carried by this dispatch instead of being sent one round trip stale.
        // It is not the only thing keeping such a change from being lost: CustomersTableFilters
        // holds `queryPending` for as long as this action runs and re-queries anything committed
        // while it is held, and it cannot tell where in that window the read below happened. So a
        // change landing anywhere in the wait is both carried here and re-queried after, one
        // redundant list request wide. Reading the filters here buys correctness for this
        // dispatch, not the saved request.
        const request = buildCustomerListRequest({
          listFilters,
          activeCompany: rootState.company.activeCompany,
          relevantCompanies: rootGetters['company/selectRelevantCompanies'],
          limit: state.limit,
          skip: state.skipCount
        })

        // No company resolves to an id the endpoint would accept, so there is nothing to ask for.
        // Settled rather than returned silently: the table renders as a skeleton for as long as
        // isSearching is true, so leaving it set is a permanent spinner. Not an error, an empty
        // state, so listError stays false.
        if (!request) {
          commit('setCountStatus', COUNT_STATUS.ABSENT)
          commit('setIsSearching', false)
          if (!loadMore) {
            commit('setCustomers', [])
            commit('setCount', 0)
          }
          return
        }

        const response = await CustomerService.listAsync(request)
        if (superseded()) return

        const requestId = response?.body?.data?.requestId
        if (!requestId) throw new Error('Customer list request returned no requestId')

        commit('setActiveRequestId', requestId)
        deliveryTimeoutId = setTimeout(() => {
          // The page may have switched to nexus since this was scheduled; a delivery for a request
          // this screen no longer owns must not touch state a different screen is now rendering.
          if (!state.useAsyncList) return

          // Guarded before the handle is released, so a timer that has been superseded cannot null
          // out the handle its successor is relying on and leave cancellation permanently dead.
          if (state.activeRequestId !== requestId) return
          deliveryTimeoutId = null

          // The count is its own message and can legitimately lag the rows, so an unarrived count
          // on its own is a missing number rather than a failed request.
          if (state.countStatus === COUNT_STATUS.PENDING) {
            commit('setCountStatus', COUNT_STATUS.ABSENT)
          }
          if (!state.isSearching) {
            // Both phases are done with, so nothing further can legitimately arrive for this id.
            commit('setActiveRequestId', null)
            return
          }

          commit('resetPendingChunks')
          commit('setListError', true)
          commit('setIsSearching', false)
          commit('setActiveRequestId', null)
          // Otherwise the table keeps rendering whatever the previous filter left behind as though
          // it belonged to this request; every other exit from this action clears or notifies.
          if (!state.pendingLoadMore) {
            commit('setCustomers', [])
            commit('setCount', 0)
          }
          Vue.notify({ type: 'error', text: DEFAULT_DELIVERY_ERROR })
        }, DELIVERY_TIMEOUT_MS)
      } catch (error) {
        if (superseded()) return

        // Status and the server's own message only. The request body carries the search term,
        // which is a guest name or phone number.
        console.error('Failed to start the customer list request', error?.status)
        Vue.notify({ type: 'error', text: dispatchErrorText(error) })
        commit('setCountStatus', COUNT_STATUS.ABSENT)
        commit('setListError', true)
        commit('setIsSearching', false)
        if (!loadMore) {
          commit('setCustomers', [])
          commit('setCount', 0)
        }
      }
    },

    receiveListRecords({ commit, state }, payload) {
      if (!payload) return
      if (!state.useAsyncList) return

      // The two shapes nest the id differently: success puts it under `data`, failure at the top
      // level. Reading it uniformly drops every failure and leaves the page waiting.
      const requestId = payload.success ? payload.data?.requestId : payload.requestId
      if (!requestId || requestId !== state.activeRequestId) return

      // The id stays live past the records phase so the count can still correlate, so matching it
      // is not on its own proof this message is wanted. Pusher delivers at least once, and a
      // redelivered chunk would otherwise append its rows to the table a second time.
      if (state.recordsComplete) return

      if (!payload.success) {
        // Whatever chunks arrived are real rows missing an arbitrary tail, which renders as a
        // plausible short table rather than something obviously broken. Discard them.
        clearDeliveryTimeout()
        commit('resetPendingChunks')
        commit('setCountStatus', COUNT_STATUS.ABSENT)
        commit('setListError', true)
        commit('setIsSearching', false)
        // Terminal: cleared so a straggling chunk cannot re-render a table this just blanked, which
        // would also hide the error card (gated on customers.length === 0).
        commit('setActiveRequestId', null)
        if (!state.pendingLoadMore) {
          commit('setCustomers', [])
          commit('setCount', 0)
        }
        Vue.notify({ type: 'error', text: payload.message || DEFAULT_DELIVERY_ERROR })
        return
      }

      const { records, skip, chunk, chunkCount } = payload.data || {}

      if (!Number.isInteger(chunk) || !Array.isArray(records)) {
        // An unvalidated chunk index or records array reaches `assembleChunks` as a `null`/`undefined`
        // entry, which the table's `v-for` then throws on mid-render with no error card and no toast.
        clearDeliveryTimeout()
        commit('resetPendingChunks')
        commit('setCountStatus', COUNT_STATUS.ABSENT)
        commit('setListError', true)
        commit('setIsSearching', false)
        commit('setActiveRequestId', null)
        if (!state.pendingLoadMore) {
          commit('setCustomers', [])
          commit('setCount', 0)
        }
        Vue.notify({ type: 'error', text: DEFAULT_DELIVERY_ERROR })
        return
      }

      commit('addPendingChunk', { chunk, records, chunkCount })

      // Fails closed: an unknown expected count means the page cannot be shown to be complete.
      if (
        !state.expectedChunkCount ||
        Object.keys(state.pendingChunks).length < state.expectedChunkCount
      ) {
        return
      }

      const assembled = assembleChunks(state.pendingChunks)
      if (state.pendingLoadMore) {
        if (assembled.length) commit('addCustomers', assembled)
      } else {
        commit('setCustomers', assembled)
      }
      commit('setLastPageSize', assembled.length)
      commit('setSkipCount', skip)
      commit('resetPendingChunks')
      commit('setRecordsComplete', true)
      commit('setIsSearching', false)
      clearDeliveryTimeoutWhenSettled(state)
    },

    receiveListCount({ commit, state }, payload) {
      if (!payload) return
      if (!state.useAsyncList) return

      // Same two shapes as the records phase: success nests the id under `data`, failure puts it at
      // the top level.
      const requestId = payload.success ? payload.data?.requestId : payload.requestId
      if (!requestId || requestId !== state.activeRequestId) return

      if (!payload.success) {
        // A count that could not be produced is a missing number, not a failed list. Settled here
        // rather than left pending so the skeleton resolves now instead of waiting out the timer.
        commit('setCountStatus', COUNT_STATUS.ABSENT)
        clearDeliveryTimeoutWhenSettled(state)
        return
      }

      const { count } = payload.data || {}

      if (count === null || count === undefined) {
        commit('setCountStatus', COUNT_STATUS.ABSENT)
      } else {
        commit('setCount', count)
        commit('setCountStatus', COUNT_STATUS.ARRIVED)
      }
      clearDeliveryTimeoutWhenSettled(state)
    },

    async getFilteredCSV({ commit, state, rootState }) {
      const response = await CustomerService.listExport({
        companyId: rootState.company.activeCompany._id,
        filters: state.listFilters
      })
      return response
    },
    async chooseWinner({ state, rootState }) {
      const response = await CustomerService.chooseWinner({
        companyId: rootState.company.activeCompany._id,
        filters: state.listFilters
      })
      const { winner } = response.body

      return winner
    },
    async getWinners({ rootState }) {
      const response = await CustomerService.pastWinners({
        companyId: rootState.company.activeCompany._id
      })
      const { winners } = response.body

      return winners
    },
    async deleteCustomer({ dispatch, commit, rootState }, customer) {
      await CustomerService.delete({
        customerId: customer._id,
        companyId: rootState.company.activeCompany._id
      })

      commit('setSkipCount', 0)
      await dispatch('queryCustomers', {})
    },

    async tagCustomer({ rootState, state, commit }, { customerId, tagId }) {
      const { activeCompany } = rootState.company

      if (!activeCompany || activeCompany._id == 'ALL_COMPANIES') return
      const companyId = activeCompany._id
      const response = await CustomerService.tagCustomer({ companyId, customerId, tagId })

      if (response.body.success !== true) {
        throw response.body
      }

      const customer = state.customers.find((c) => c._id === customerId)
      if (customer) {
        const currentTags = (customer.tags || {})[companyId] || []
        commit('updateCustomer', {
          ...customer,
          tags: { ...customer.tags, [companyId]: [...new Set([...currentTags, tagId])] }
        })
      }
    },
    async untagCustomer({ rootState, state, commit }, { customerId, tagId }) {
      const { activeCompany } = rootState.company

      if (!activeCompany || activeCompany._id == 'ALL_COMPANIES') return
      const companyId = activeCompany._id
      const response = await CustomerService.untagCustomer({ companyId, customerId, tagId })

      if (response.body.success !== true) {
        throw response.body
      }

      const customer = state.customers.find((c) => c._id === customerId)
      if (customer) {
        const currentTags = (customer.tags || {})[companyId] || []
        commit('updateCustomer', {
          ...customer,
          tags: { ...customer.tags, [companyId]: currentTags.filter((t) => t !== tagId) }
        })
      }
    },

    async blockCustomer(
      { commit, dispatch },
      { companyId, customerId, conversationId, customerObj }
    ) {
      const response = await CustomerService.blockCustomer({ companyId, customerId })

      if (response.body.success !== true) {
        throw response.body
      } else if (conversationId && customerObj) {
        await dispatch(
          'conversationV2/conversationChange',
          {
            conversationData: {
              _id: conversationId,
              customer: { ...customerObj, blocked: [...customerObj.blocked, companyId] }
            },
            trace: 'blockCustomer'
          },
          { root: true }
        )
      }
    },

    async unblockCustomer(
      { commit, dispatch },
      { companyId, customerId, conversationId, customerObj }
    ) {
      const response = await CustomerService.unblockCustomer({ companyId, customerId })

      if (response.body.success !== true) {
        throw response.body
      } else if (conversationId && customerObj) {
        await dispatch(
          'conversationV2/conversationChange',
          {
            conversationData: {
              _id: conversationId,
              customer: {
                ...customerObj,
                blocked: customerObj.blocked.filter((c) => c !== companyId)
              }
            },
            trace: 'unblockCustomer'
          },
          { root: true }
        )
      }
    },

    async fetchMetrics({ commit }, { customerId, companyId }) {
      const response = await CustomerService.fetchMetrics({ customerId, companyId })
      commit('SET_METRICS', response.body.data.metrics)
    },

    setImportParams({ commit }, importParams) {
      commit('SET_IMPORT_PARAMS', importParams)
    },

    async importCustomers({ getters, rootGetters }, { mappedFields, preflight, importId }) {
      await CustomerService.import({
        ...getters.selectImportParams,
        mappedFields,
        preflight,
        importId,
        // TODO(OVA-5166): remove. nexus now reads the user from the token and ignores this; it is
        // kept only while old nexus deploys are still live.
        userId: rootGetters['user/onlineUser']._id
      })
    },

    async setListFilters({ commit }, filters) {
      commit('SET_LIST_FILTERS', filters)
    },

    async setSkipCount({ commit }, skip) {
      commit('SET_SKIP_COUNT', skip)
    }
  }
}
